import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { UsersTab, ReviewsTab, ContactsTab } from "@/components/admin/AdminTabs";
import { ShowcaseTab } from "@/components/admin/ShowcaseTab";
import { SettingsTab } from "@/components/admin/SettingsTab";
import { AuraMark } from "@/components/site/AuraMark";

export default function Admin() {
  const { t } = useLang();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ users: [], reviews: [], contacts: [], showcase: {} });

  const load = useCallback(async () => {
    try {
      const [s, u, r, c, sh] = await Promise.all([api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/reviews"), api.get("/admin/contacts"), api.get("/showcase")]);
      setStats(s.data);
      setData({ users: u.data ?? [], reviews: r.data ?? [], contacts: c.data ?? [], showcase: sh.data ?? {} });
    } catch (e) {
      toast.error(errMsg(e, t.common.error));
    }
  }, [t.common.error]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  const act = async (method, url, body) => {
    try {
      await api[method](url, body);
      await load();
    } catch (e) {
      toast.error(errMsg(e, t.common.error));
    }
  };

  if (user === null) return <div className="min-h-screen grid place-items-center"><AuraMark className="w-12 h-12 animate-pulse" /></div>;
  if (user?.role !== "admin") return <AdminLogin />;

  const a = t.admin;
  return (
    <main className="min-h-screen pt-[100px] pb-20 px-5 md:px-10" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-sky-300">Aura_ · Owner</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl font-extrabold text-white">{a.title}</h1>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(a.stats).map(([k, label]) => (
            <div key={k} className="bento-card rounded-2xl p-5" data-testid={`admin-stat-${k}`}>
              <p className="font-display text-4xl font-extrabold text-white">{stats?.[k] ?? "—"}</p>
              <p className="mt-1 text-sm text-slate-400">{label}</p>
            </div>
          ))}
        </div>
        <Tabs defaultValue="users" className="mt-10">
          <TabsList className="bg-white/5 border border-white/10 rounded-full p-1 h-auto flex-wrap">
            {Object.entries(a.tabs).map(([k, label]) => (
              <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`} className="rounded-full px-5 py-2 text-slate-300 data-[state=active]:bg-white data-[state=active]:text-[#050713]">{label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="users"><UsersTab items={data.users} a={a} act={act} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab items={data.reviews} a={a} act={act} /></TabsContent>
          <TabsContent value="contacts"><ContactsTab items={data.contacts} a={a} act={act} /></TabsContent>
          <TabsContent value="showcase"><ShowcaseTab data={data.showcase} reload={load} /></TabsContent>
          <TabsContent value="settings"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
